import { Link, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOpenNoteForBooking } from '@/hooks/use-sessions';
import type { BookingSessionNoteSummary } from '@/types/sessions';
import { EMPTY_DOC } from './noteMarks';
import { NoteStatusChip } from './shared';
import { noteCellState } from './noteCellState';

interface BookingNoteCellProps {
  bookingId: string | number;
  summary: BookingSessionNoteSummary | null | undefined;
  startTime?: string;
  status: string;
  /** Touch-sized button for the phone list. */
  large?: boolean;
}

/**
 * The session-note cell of a booking row on the customer page. An existing
 * note opens from its status chip; a button appears only where a note is
 * still due. Other therapists' notes show their status behind a lock.
 */
export function BookingNoteCell({ bookingId, summary, startTime, status, large = false }: BookingNoteCellProps) {
  const { t } = useTranslation('sessions');
  const [, setLocation] = useLocation();
  const openNote = useOpenNoteForBooking();
  const state = noteCellState(summary, startTime, status);

  switch (state.kind) {
    case 'open':
      return (
        <Link
          href={`/admin/sessions/notes/${state.noteId}`}
          className="inline-flex items-center gap-0.5 rounded-pill text-soft hover:text-ink"
        >
          <NoteStatusChip status={state.status} />
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      );
    case 'locked':
      return (
        <span className="inline-flex items-center gap-1.5" title={t('customerTab.lockedHint')}>
          <Lock className="h-3.5 w-3.5 text-text-muted" aria-label={t('customerTab.lockedHint')} />
          <NoteStatusChip status={state.status} />
        </span>
      );
    case 'write':
      return (
        <span className="inline-flex flex-wrap items-center gap-2">
          {state.missing && (
            <span className="inline-flex items-center whitespace-nowrap rounded-pill bg-warn-soft px-2.5 py-1 text-xs font-semibold text-warn-text">
              {t('customerTab.noteMissing')}
            </span>
          )}
          <Button
            variant="outline-brand"
            size={large ? 'md' : 'sm'}
            loading={openNote.isPending}
            onClick={() =>
              openNote.mutate(
                { bookingId, content: EMPTY_DOC },
                { onSuccess: (note) => setLocation(`/admin/sessions/notes/${note.id}`) },
              )
            }
          >
            {t('common.write')}
          </Button>
        </span>
      );
    case 'later':
      return <span className="text-xs text-soft">{t('customerTab.afterSession')}</span>;
    default:
      return <span className="text-text-muted">–</span>;
  }
}
